import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Grid } from '@mui/material';
import { StylovyzeTable, useUnits } from '@innovyze/stylovyze';
import { timeAgo } from '@Utils/datetime';
import { AssetFilter } from '@Types/asset.types';
import { useGlobalization } from '@Translations/useGlobalization';
import { selectAssetStore } from '@Selectors/asset.selectors';
import { getAssetFields } from '@Actions/asset.actions';

const StyledFilterInfo = styled.p({
	display: 'block',
	width: '100%',
	marginBottom: 0,
	'& small': {
		display: 'block',
		opacity: 0.6,
		paddingTop: '0.2em',
	},
});

export interface RiskSetupAssetFilterProps {
	assetFilter: AssetFilter;
}

export const RiskSetupAssetFilter = ({
	assetFilter,
}: RiskSetupAssetFilterProps) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();

	const { systemType, assetType } = assetFilter;

	useEffect(() => {
		dispatch(getAssetFields({ systemType, assetType }));
	}, []);

	const { system, getSystemValue, getSystemUnit } = useUnits();
	const { assetFields } = selectAssetStore();

	const filterMeta = (f: AssetFilter) =>
		`Created ${timeAgo(f.dateCreated)} by ${String(f.createdBy)
			.split('@')
			.shift()}`;

	const toDisplayName = (fieldName: string) => {
		if (fieldName === 'spatial') {
			return 'Spatial';
		}
		if (assetFields) {
			const match = assetFields.find(f => f.name === fieldName);
			if (match && match.displayName) return match.displayName;
		}
		return fieldName;
	};

	const toDisplayValue = (
		fieldName: string,
		value: string | object | undefined,
	) => {
		if (!value) return '';
		if (typeof value !== 'string') return 'Area';
		if (assetFields && system !== 'Metric') {
			const match = assetFields.find(f => f.name === fieldName);
			// Try convert fields using decimals and UOM
			if (match && match.type === 'Double' && match.units) {
				const units = getSystemUnit(match.units);
				const metricValue = `${value} ${match.units}`;
				if (units) {
					const systemValue = getSystemValue(metricValue);
					return `${systemValue} ${units}`;
				}
				return metricValue;
			}
		}
		return value;
	};

	return (
		<Grid item xs={12} style={{ paddingTop: '20px' }}>
			<StylovyzeTable
				className="ScrollTable"
				title={t('Asset Filter')}
				titleArea={
					<StyledFilterInfo>
						{assetFilter.name}
						<small>{filterMeta(assetFilter)}</small>
					</StyledFilterInfo>
				}
				dataCy="riskFilter-table"
				headers={[
					{
						key: 'field',
						cell: t('Field'),
						sortable: false,
					},
					{
						key: 'operator',
						cell: t('Operator'),
						sortable: false,
					},
					{
						key: 'value',
						cell: t('Value'),
						sortable: false,
					},
				]}
				rows={assetFilter.filters.map((f, key) => ({
					key,
					cells: [
						toDisplayName(f.field),
						f.operator,
						toDisplayValue(f.field, f.value),
					],
				}))}></StylovyzeTable>
		</Grid>
	);
};
